import { useEffect, useState } from "react";
import { FileSpreadsheet, Download } from "lucide-react";
import { Page, Panel } from "../lib/ui";
import { api, money, download } from "../lib/api";

const MONTHS = ["", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export default function TaxReport() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState("");
  const [data, setData] = useState({ months: [], rates: [], totals: {}, invoice_count: 0 });

  const load = async () => {
    const params = { year, ...(month && { month }) };
    const { data } = await api.get("/reports/kdv", { params });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const doExport = (fmt) => {
    const qs = `year=${year}${month ? `&month=${month}` : ""}&fmt=${fmt}`;
    download(`/reports/kdv/export?${qs}`, `kdv-raporu-${year}${month ? `-${String(month).padStart(2, "0")}` : ""}.${fmt}`);
  };

  const t = data.totals || {};

  return (
    <>
      <Page
        eyebrow="MUHASEBE · KDV"
        title="Vergi Raporu"
        subtitle="Aylık KDV toplamlarını fatura türüne ve orana göre inceleyin, muhasebeciye tek tıkla iletin."
        action={
          <div className="button-row">
            <button data-testid="tax-export-xlsx" className="secondary-button" onClick={() => doExport("xlsx")}>
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button data-testid="tax-export-csv" className="primary-button" onClick={() => doExport("csv")}>
              <Download size={14} /> CSV indir
            </button>
          </div>
        }
      />

      <div className="filter-row" style={{ marginBottom: 18 }}>
        <label className="field inline-field">
          <span>Yıl</span>
          <select data-testid="tax-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="field inline-field">
          <span>Ay</span>
          <select data-testid="tax-month" value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">Tüm yıl</option>
            {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </label>
      </div>

      <div className="mini-stats">
        <div><span>Resmi matrah</span><strong>{money(t.resmi_base || 0)}</strong></div>
        <div><span>Resmi KDV</span><strong className="negative">{money(t.resmi_kdv || 0)}</strong></div>
        <div><span>Proforma matrah</span><strong>{money(t.proforma_base || 0)}</strong></div>
        <div><span>Fatura sayısı</span><strong data-testid="tax-invoice-count">{data.invoice_count}</strong></div>
      </div>

      <div className="dashboard-grid">
        <Panel title="Aylara göre KDV" subtitle="Resmi ve Proforma matrah/KDV" className="module-panel">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Ay</th>
                <th className="amount">Resmi matrah</th><th className="amount">Resmi KDV</th>
                <th className="amount">Proforma matrah</th><th className="amount">Proforma KDV</th>
              </tr></thead>
              <tbody>
                {data.months.length === 0 && (
                  <tr><td colSpan="5" className="empty">Bu dönemde fatura yok.</td></tr>
                )}
                {data.months.map((m) => (
                  <tr key={m.month} data-testid={`tax-month-${m.month}`}>
                    <td><strong>{m.month}</strong></td>
                    <td className="amount">{money(m.Resmi.base)}</td>
                    <td className="amount negative">{money(m.Resmi.kdv)}</td>
                    <td className="amount">{money(m.Proforma.base)}</td>
                    <td className="amount">{money(m.Proforma.kdv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="KDV oranı dağılımı" subtitle="%0 / %1 / %10 / %20 kırılımı" className="module-panel">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Oran</th><th>Kalem</th><th className="amount">Matrah</th><th className="amount">KDV</th></tr></thead>
              <tbody>
                {(() => {
                  const rateMap = Object.fromEntries((data.rates || []).map((r) => [r.rate, r]));
                  const displayRates = ["%0", "%1", "%10", "%20"];
                  const rows = displayRates.map((rate) => rateMap[rate] || { rate, count: 0, base: 0, kdv: 0 });
                  const totalBase = rows.reduce((s, r) => s + Number(r.base || 0), 0);
                  const totalKdv = rows.reduce((s, r) => s + Number(r.kdv || 0), 0);
                  const totalCount = rows.reduce((s, r) => s + Number(r.count || 0), 0);
                  return (
                    <>
                      {rows.map((r) => (
                        <tr key={r.rate} data-testid={`tax-rate-${r.rate}`}>
                          <td><strong>{r.rate}</strong></td>
                          <td>{r.count}</td>
                          <td className="amount">{money(r.base)}</td>
                          <td className="amount negative">{money(r.kdv)}</td>
                        </tr>
                      ))}
                      <tr className="row-total" data-testid="tax-rate-total">
                        <td><strong>Toplam</strong></td>
                        <td><strong>{totalCount}</strong></td>
                        <td className="amount"><strong>{money(totalBase)}</strong></td>
                        <td className="amount negative"><strong>{money(totalKdv)}</strong></td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}
